import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { Button } from '../../components/Button';
import { H2 } from '../../components/Typography';
import { TextArea } from '../../components/TextArea';
import { interpretGraphqlError } from '../../components/ErrorMessage';
import { createCancellationRequestMutation } from './mutations';
import { REQUEST_NOTES_MAX_LENGTH } from './constants';
import { css } from 'glamor';
import { withInstallation } from "../../components/InstallationContext";
import { useMutation } from "@apollo/client";

const RequestCancellationForm = ({ onSubmit, onCancel, bookingId, notes, onNotesChange, isSubmitting }) => {
	return(
        <div>
            <H2 spaceAbove="none">Request Cancellation</H2>

            <p>Are you sure you want to cancel this booking?</p>
            <TextArea
                label="Notes (optional)"
                placeholder="Reason for the cancellation, number of days to cancel and refund, any other details"
                size="small"
                rows="4"
                maxLength={REQUEST_NOTES_MAX_LENGTH}
                className={css({ '& textarea': { height: 'auto', paddingTop: 8, paddingBottom: 8 } })}
                value={notes}
                onValueChange={onNotesChange}
            />
            <Button
                spaceBelow="none"
                disabled={isSubmitting}
                onClick={() => {
                    onSubmit(bookingId);
                }}
            >
                Yes
            </Button>
            <Button
                spaceBelow="none"
                mode="hollow"
                flavour="danger"
                onClick={onCancel}
                className={css({ marginLeft: "5px" })}
            >
                No
            </Button>
        </div>
    )
};

const ErrorMessage = ({ onClose, error }) => {
	return(
        <div className={css({ textAlign: 'center' })}>
            <H2 spaceAbove="medium">Something went wrong.</H2>
            <p>{error}</p>
            <Button spaceAbove="medium" onClick={onClose}>
                Confirm
            </Button>
        </div>
    )
}

const _RequestCancellation = ({ booking, onCancellationRequest, isCancellationAlreadyRequested, isCancellationProcessed }) => {
	const [isFormOpen, setIsFormOpen] = useState(false);
    const [error, setError] = useState(null);
    const [notes, setNotes] = useState('');
    const [createCancellationRequest, { loading: isSubmitting } = {}] = useMutation(createCancellationRequestMutation);

    /**
     * The note lives here, not in the form, because a failed request swaps the
     * form out for the error message. Dismissing that error keeps the note so it
     * does not have to be retyped; closing the form itself discards it.
     */
	const toggleForm = () => {
        setIsFormOpen(!isFormOpen);
        if (error) setError(null);
        else if (isFormOpen) setNotes('');
    }

	const onSuccess = () => {
		onCancellationRequest()
		toggleForm()
	}

    const handleSubmit = async (bookingId) => {
        const trimmedNotes = notes.trim();

        try {
            await createCancellationRequest({
                variables: {
                    relatedBookingId: bookingId,
                    ...(trimmedNotes ? { notes: trimmedNotes } : {}),
                },
            });
        
            onSuccess()
        } catch (error) {
            /**
             * The API answers with a non-2xx status, which Apollo reports as
             * "Response not successful"; the real reason is inside the response.
             * Refetching updates the button when the failure is "already requested".
             */
            setError(interpretGraphqlError(error));
            onCancellationRequest()
        }
    }

	return (
		<div>
			<Button block mode="hollow" flavour="warning" onClick={toggleForm} disabled={isCancellationAlreadyRequested}>
                { !isCancellationAlreadyRequested ? "Request Cancellation" : isCancellationProcessed ? "Booking Cancelled" : "Booking Cancellation In Progress" }
			</Button>
			<ReactModal
				isOpen={isFormOpen}
				onRequestClose={toggleForm}
				className={css({
					display: 'block',
					background: 'white',
					margin: 10,
					padding: 20,
					borderRadius: 6,
					outline: 'none',
					minWidth: 400,
				}).toString()}
				overlayClassName={css({
					backgroundColor: 'rgba(0,0,0,0.2)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					position: 'fixed',
					zIndex: 100000,
					top: 0,
					right: 0,
					bottom: 0,
					left: 0,
				}).toString()}
			>
                { isFormOpen && !error &&
                    <RequestCancellationForm
                        onSubmit={handleSubmit}
                        onCancel={toggleForm}
                        bookingId={booking?.id}
                        notes={notes}
                        onNotesChange={setNotes}
                        isSubmitting={isSubmitting}
                    />
                }
                { error &&
                    <ErrorMessage onClose={toggleForm} error={error} />
                }
			</ReactModal>
		</div>
	);
}

export default withInstallation(_RequestCancellation);
